/**
 * TemplateCard Component (Stories 5.3, 5.14, AC #2, #4, #7)
 *
 * Flat card with left-side category color accent, hover lift + overlay,
 * platform pills, locked state for plan-gated templates, and
 * Edit/Delete actions for custom templates.
 */

import { useNavigate } from "@tanstack/react-router";
import {
  Lock,
  Check,
  Warning,
  PencilSimple,
  Trash,
  Sparkle,
  Eye,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@provenleap/ui";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import { getRequiredPlatforms } from "../../lib/templateUtils";
import type { ParsedTemplate } from "../../lib/templateUtils";

const CATEGORY_COLORS: Record<string, string> = {
  analytics: "bg-blue-100 text-blue-700",
  seo: "bg-green-100 text-green-700",
  advertising: "bg-orange-100 text-orange-700",
  social: "bg-purple-100 text-purple-700",
  ecommerce: "bg-emerald-100 text-emerald-700",
  "multi-channel": "bg-indigo-100 text-indigo-700",
};

const CATEGORY_BORDER_COLORS: Record<string, string> = {
  analytics: "border-l-blue-400",
  seo: "border-l-green-400",
  advertising: "border-l-orange-400",
  social: "border-l-purple-400",
  ecommerce: "border-l-emerald-400",
  "multi-channel": "border-l-indigo-400",
};

export function PlatformPill({
  platformId,
  isConnected,
}: {
  platformId: string;
  isConnected: boolean;
}) {
  const platform = PICKER_PLATFORMS.find((p) => p.platform === platformId);
  const displayName = platform?.displayName ?? platformId;
  const domain = platform?.domain;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
        isConnected
          ? "bg-green-50 text-green-700"
          : "bg-amber-50 text-amber-700"
      }`}
    >
      {domain && (
        <img
          src={logoUrl(domain)}
          alt=""
          className="w-3.5 h-3.5"
          loading="lazy"
        />
      )}
      <span className="truncate max-w-[80px]">{displayName}</span>
      {isConnected ? (
        <Check size={12} weight="bold" aria-label="Connected" />
      ) : (
        <Warning size={12} weight="bold" aria-label="Missing" />
      )}
    </span>
  );
}

interface TemplateCardProps {
  template: ParsedTemplate;
  connectedPlatforms: string[];
  onSelect: (id: string) => void;
  onPreview?: (id: string) => void;
  onEdit?: (template: ParsedTemplate) => void;
  onDelete?: (template: ParsedTemplate) => void;
  isRecommended?: boolean;
}

export function TemplateCard({
  template,
  connectedPlatforms,
  onSelect,
  onPreview,
  onEdit,
  onDelete,
  isRecommended,
}: TemplateCardProps) {
  const navigate = useNavigate();
  const isCustom = template.isSystemTemplate === 0;
  const category = template.category ?? "analytics";
  const badgeColor = CATEGORY_COLORS[category] ?? "bg-gray-100 text-gray-700";
  const borderColor =
    CATEGORY_BORDER_COLORS[category] ?? "border-l-gray-400";

  const requiredPlatforms = getRequiredPlatforms(template.parsedLayout);
  const hasMissingIntegrations =
    !template.locked &&
    requiredPlatforms.length > 0 &&
    requiredPlatforms.some((p) => !connectedPlatforms.includes(p));

  const handleClick = () => {
    if (template.locked) {
      navigate({ to: "/settings/billing" });
      return;
    }
    onSelect(template.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const ariaLabel = template.locked
    ? `${template.name} - Locked. Upgrade to Team to use this template.`
    : template.name;

  return (
    <motion.div
      whileTap={template.locked ? undefined : { scale: 1.02 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-disabled={template.locked ? "true" : undefined}
        className={`group relative rounded-lg border border-l-[3px] ${borderColor} bg-card overflow-hidden transition-all duration-200 ${
          template.locked
            ? "opacity-70"
            : "hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
        }`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-testid={`template-card-${template.id}`}
      >
        {/* Recommended badge */}
        {isRecommended && (
          <span
            className="absolute top-2 right-2 z-20 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary animate-pulse"
            data-testid="recommended-badge"
          >
            <Sparkle size={10} weight="fill" />
            Recommended
          </span>
        )}

        {/* Hover overlay with Preview + Use Template buttons */}
        {!template.locked && (
          <div
            className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-3 rounded-lg z-10"
            data-testid={`template-overlay-${template.id}`}
          >
            {onPreview && (
              <Button
                size="sm"
                variant="outline"
                className="bg-white hover:bg-white/90 text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview(template.id);
                }}
                data-testid={`preview-template-${template.id}`}
              >
                <Eye size={14} className="mr-1.5" />
                Preview
              </Button>
            )}
            <Button
              size="sm"
              variant="brand"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(template.id);
              }}
              data-testid={`use-template-${template.id}`}
            >
              Use Template
            </Button>
          </div>
        )}

        <div className="px-4 py-3">
          {/* Top row: title + actions */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {template.locked && (
                <Lock size={14} className="shrink-0 text-muted-foreground" />
              )}
              <h3 className="font-semibold text-sm truncate">{template.name}</h3>
            </div>

            {/* Edit/Delete actions for custom templates */}
            {isCustom && (onEdit || onDelete) && (
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 z-20">
                {onEdit && (
                  <button
                    data-testid={`edit-template-${template.id}`}
                    aria-label={`Edit ${template.name}`}
                    className="p-1 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(template);
                    }}
                  >
                    <PencilSimple size={14} />
                  </button>
                )}
                {onDelete && (
                  <button
                    data-testid={`delete-template-${template.id}`}
                    aria-label={`Delete ${template.name}`}
                    className="p-1 rounded-md hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(template);
                    }}
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
            )}
          </div>

          {template.description && (
            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
              {template.description}
            </p>
          )}

          {/* Category badge + warning badge */}
          <div className="mt-2 flex items-center gap-2">
            <span
              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}
            >
              {category}
            </span>
            {hasMissingIntegrations && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700"
                data-testid="missing-integrations-badge"
              >
                <Warning size={12} weight="bold" />
                Missing integrations
              </span>
            )}
          </div>

          {/* Platform pills */}
          {!template.locked && requiredPlatforms.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {requiredPlatforms.map((platformId) => (
                <PlatformPill
                  key={platformId}
                  platformId={platformId}
                  isConnected={connectedPlatforms.includes(platformId)}
                />
              ))}
            </div>
          )}

          {/* CTA Button */}
          <div className="mt-3">
            {template.locked ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate({ to: "/settings/billing" });
                }}
              >
                Upgrade to Team
              </Button>
            ) : (
              <Button
                size="sm"
                variant="brand"
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(template.id);
                }}
              >
                Use This Template
              </Button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}